/* The app scrolls inside one element (#app-scroll), not the document.
   On iOS a fast flick on a scrolling document can drag position:fixed bars
   (the tab bar, the mini player) up the screen and leave them there — WebKit
   re-anchors fixed elements to a viewport that moved with the overscroll.
   With the document itself never scrolling, fixed bars have nothing to move
   with. Everything that scrolled `window` goes through here instead. */

export const SCROLL_ID = 'app-scroll';

/** The page scroller — the document's own scroller outside the app shell. */
export function scroller(): HTMLElement {
  return document.getElementById(SCROLL_ID) ?? (document.scrollingElement as HTMLElement) ?? document.documentElement;
}

export const scrollTop = () => scroller().scrollTop;

let anim = 0;
let stopAnim: (() => void) | null = null;

/** Scroll the page. `smooth` is animated here rather than with
    behavior:'smooth' — WebKit drops a native smooth scroll on an element when
    a re-render lands mid-way (tap a date → the editor re-renders → stuck). */
export function scrollPageTo(top: number, smooth = false) {
  const el = scroller();
  stopAnim?.();
  const to = Math.max(0, Math.min(top, el.scrollHeight - el.clientHeight));
  const from = el.scrollTop;
  const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!smooth || reduce || Math.abs(to - from) < 2) { el.scrollTop = to; return; }
  const start = performance.now();
  const ms = Math.min(600, 250 + Math.abs(to - from) / 6);
  // Your finger wins: touching or wheeling stops the animation.
  const stop = () => { cancelAnimationFrame(anim); el.removeEventListener('touchstart', stop); el.removeEventListener('wheel', stop); if (stopAnim === stop) stopAnim = null; };
  el.addEventListener('touchstart', stop, { passive: true });
  el.addEventListener('wheel', stop, { passive: true });
  stopAnim = stop;
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / ms);
    el.scrollTop = from + (to - from) * (1 - Math.pow(1 - t, 3)); // ease-out
    if (t < 1) anim = requestAnimationFrame(step); else stop();
  };
  anim = requestAnimationFrame(step);
}
